import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { church } from "@/config/church";
import useNoIndex from "@/hooks/useNoIndex";
import {
  LogOut,
  Calendar,
  Radio,
  Megaphone,
  Mail,
  HandHeart,
  UserPlus,
  Heart,
  BarChart3,
  Plus,
  Trash2,
  Pencil,
  ExternalLink,
  X,
  Check,
} from "lucide-react";

const TABS = [
  { id: "stats",   label: "Overview",       icon: BarChart3 },
  { id: "events",  label: "Events",         icon: Calendar },
  { id: "sermons", label: "Sermons",        icon: Radio },
  { id: "ann",     label: "Announcements",  icon: Megaphone },
  { id: "prayer",  label: "Prayer Requests",icon: HandHeart },
  { id: "contact", label: "Contact",        icon: Mail },
  { id: "reg",     label: "Registrations",  icon: UserPlus },
  { id: "give",    label: "Donation Intents", icon: Heart },
  { id: "news",    label: "Newsletter",     icon: Mail },
];

export default function AdminDashboard() {
  useNoIndex();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  const doLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-npbc-cream">
      <header className="sticky top-0 z-30 bg-white border-b border-black/[0.06]">
        <div className="container-npbc flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-header-logo">
            <img src={church.logo} alt="NPBC" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="heading-serif text-base font-bold text-npbc-black">New Philadelphia</div>
              <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-red">Admin Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer noopener" className="text-xs text-npbc-gray hover:text-npbc-red inline-flex items-center gap-1.5">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <div className="hidden sm:block text-xs text-npbc-gray/70">{user?.email}</div>
            <button
              type="button"
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-semibold hover:bg-npbc-black hover:text-white transition"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-npbc py-8 grid gap-8 lg:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <nav className="lg:sticky lg:top-24 self-start">
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-left transition ${
                  tab === t.id ? "bg-npbc-red text-white shadow-md" : "bg-white text-npbc-black hover:bg-npbc-black hover:text-white"
                }`}
              >
                <t.icon className="h-4 w-4" />
                <span className="hidden lg:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <main className="min-w-0">
          {tab === "stats"   && <Overview />}
          {tab === "events"  && <EventsPanel />}
          {tab === "sermons" && <SermonsPanel />}
          {tab === "ann"     && <AnnouncementsPanel />}
          {tab === "prayer"  && <SimpleList endpoint="/admin/prayer-requests" title="Prayer Requests" fields={["name", "email", "request", "is_public", "is_urgent"]} />}
          {tab === "contact" && <SimpleList endpoint="/admin/contact-messages" title="Contact Messages" fields={["name", "email", "phone", "subject", "message"]} />}
          {tab === "reg"     && <SimpleList endpoint="/admin/registrations" title="Registrations" fields={["kind", "name", "email", "phone", "notes"]} />}
          {tab === "give"    && <SimpleList endpoint="/admin/donation-intents" title="Donation Intents" fields={["name", "email", "amount", "fund", "frequency"]} />}
          {tab === "news"    && <SimpleList endpoint="/admin/newsletter" title="Newsletter Subscribers" fields={["email", "name"]} />}
        </main>
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
}

// ---------------- Overview ----------------
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const items = [
    { key: "events", label: "Events", icon: Calendar, color: "bg-npbc-red/10 text-npbc-red" },
    { key: "sermons", label: "Sermons", icon: Radio, color: "bg-npbc-gold/20 text-npbc-black" },
    { key: "announcements", label: "Announcements", icon: Megaphone, color: "bg-npbc-black/10 text-npbc-black" },
    { key: "prayer_requests", label: "Prayer Requests", icon: HandHeart, color: "bg-red-50 text-red-700" },
    { key: "contact_messages", label: "Contact Messages", icon: Mail, color: "bg-amber-50 text-amber-700" },
    { key: "registrations", label: "Registrations", icon: UserPlus, color: "bg-emerald-50 text-emerald-700" },
    { key: "donation_intents", label: "Donation Intents", icon: Heart, color: "bg-rose-50 text-rose-700" },
    { key: "newsletter", label: "Newsletter", icon: Mail, color: "bg-sky-50 text-sky-700" },
  ];
  return (
    <div>
      <h1 className="heading-serif text-4xl">Welcome back, Pastor.</h1>
      <p className="mt-2 text-npbc-gray">Here is what's happening across your church website.</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((i) => (
          <div key={i.key} className="rounded-2xl bg-white p-6 border border-black/[0.05]">
            <div className={`h-10 w-10 rounded-xl grid place-items-center ${i.color}`}>
              <i.icon className="h-4 w-4" />
            </div>
            <div className="mt-4 heading-serif text-4xl">{stats ? stats[i.key] ?? 0 : "—"}</div>
            <div className="mt-1 text-xs uppercase tracking-[0.22em] text-npbc-gray/70 font-semibold">{i.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Events ----------------
const EVENT_FIELDS = [
  { key: "title", label: "Title" },
  { key: "date", label: "Date (e.g., Every Sunday or 2026-02-14)" },
  { key: "time", label: "Time (e.g., 11:00 AM)" },
  { key: "location", label: "Location" },
  { key: "category", label: "Category (Worship / Discipleship / Fellowship / Youth)" },
  { key: "description", label: "Description", textarea: true },
  { key: "image", label: "Image URL" },
];

function EventsPanel() {
  return <CrudPanel title="Events" endpoint="/admin/events" listEndpoint="/events" fields={EVENT_FIELDS} testKey="event" />;
}

// ---------------- Sermons ----------------
const SERMON_FIELDS = [
  { key: "title", label: "Title" },
  { key: "speaker", label: "Speaker" },
  { key: "series", label: "Series" },
  { key: "date", label: "Date (YYYY-MM-DD)" },
  { key: "scripture", label: "Scripture" },
  { key: "duration", label: "Duration (mm:ss)" },
  { key: "thumbnail", label: "Thumbnail URL" },
  { key: "video_url", label: "Video URL (Facebook / YouTube link)" },
  { key: "description", label: "Description", textarea: true },
];

function SermonsPanel() {
  return <CrudPanel title="Sermons" endpoint="/admin/sermons" listEndpoint="/sermons" fields={SERMON_FIELDS} testKey="sermon" />;
}

// ---------------- Announcements ----------------
const ANN_FIELDS = [
  { key: "title", label: "Title" },
  { key: "body", label: "Message", textarea: true },
  { key: "link_label", label: "Button label (optional)" },
  { key: "link_url", label: "Button URL (optional)" },
];

function AnnouncementsPanel() {
  return (
    <CrudPanel
      title="Announcements"
      endpoint="/admin/announcements"
      listEndpoint="/admin/announcements"
      fields={ANN_FIELDS}
      extraToggle={{ key: "is_active", label: "Active (show on site)" }}
      testKey="ann"
    />
  );
}

// ---------------- Generic CRUD ----------------
function CrudPanel({ title, endpoint, listEndpoint, fields, extraToggle, testKey }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | object

  const load = async () => {
    try {
      const { data } = await api.get(listEndpoint);
      setItems(data || []);
    } catch {
      toast.error(`Could not load ${title.toLowerCase()}.`);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => {
    const blank = fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
    if (extraToggle) blank[extraToggle.key] = true;
    setEditing({ ...blank, __new: true });
  };
  const openEdit = (item) => setEditing({ ...item });

  const save = async () => {
    setBusy(true);
    try {
      if (editing.__new) {
        const payload = { ...editing }; delete payload.__new;
        await api.post(endpoint, payload);
        toast.success("Created.");
      } else {
        const payload = { ...editing }; delete payload.__new;
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Updated.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title || item.name || item.id}"?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Deleted.");
      await load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-serif text-4xl">{title}</h1>
          <p className="mt-1 text-sm text-npbc-gray">{items.length} total</p>
        </div>
        <button type="button" onClick={openNew} data-testid={`admin-${testKey}-new`} className="btn-primary !py-2.5 !text-sm">
          <Plus className="h-4 w-4" /> New
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="rounded-2xl bg-white border border-black/[0.05] p-5 flex items-start gap-4">
            {item.image || item.thumbnail ? (
              <img src={item.image || item.thumbnail} alt="" className="h-16 w-24 rounded-lg object-cover" />
            ) : (
              <div className="h-16 w-24 rounded-lg bg-npbc-cream grid place-items-center text-npbc-red">
                <Megaphone className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="heading-serif text-lg leading-tight">{item.title}</div>
              <div className="text-xs text-npbc-gray/80 mt-0.5">
                {item.date && <>{item.date} · </>}
                {item.time && <>{item.time} · </>}
                {item.speaker && <>{item.speaker} · </>}
                {item.series && <>{item.series} · </>}
                {item.location && <>{item.location}</>}
              </div>
              {(item.description || item.body) && (
                <div className="text-sm text-npbc-gray mt-1 line-clamp-2">{item.description || item.body}</div>
              )}
              {extraToggle && (
                <div className={`inline-flex items-center gap-1.5 mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] ${item[extraToggle.key] ? "text-emerald-600" : "text-npbc-gray/60"}`}>
                  {item[extraToggle.key] ? <><Check className="h-3 w-3" /> Active</> : <><X className="h-3 w-3" /> Hidden</>}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button type="button" onClick={() => openEdit(item)} data-testid={`admin-${testKey}-edit-${item.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold hover:bg-npbc-black hover:text-white transition">
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button type="button" onClick={() => remove(item)} data-testid={`admin-${testKey}-delete-${item.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 text-red-700 px-3 py-1.5 text-xs font-semibold hover:bg-red-600 hover:text-white hover:border-red-600 transition">
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="rounded-2xl bg-white border border-dashed border-black/10 p-12 text-center text-npbc-gray/70">
            Nothing here yet — click "New" to add one.
          </div>
        )}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.__new ? `New ${title.replace(/s$/, "")}` : `Edit ${title.replace(/s$/, "")}`}>
          <div className="space-y-4">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-npbc-red mb-1.5">{f.label}</div>
                {f.textarea ? (
                  <textarea
                    rows={4}
                    value={editing[f.key] || ""}
                    onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                    data-testid={`admin-${testKey}-field-${f.key}`}
                    className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20"
                  />
                ) : (
                  <input
                    type="text"
                    value={editing[f.key] || ""}
                    onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                    data-testid={`admin-${testKey}-field-${f.key}`}
                    className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20"
                  />
                )}
              </label>
            ))}
            {extraToggle && (
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing[extraToggle.key]}
                  onChange={(e) => setEditing({ ...editing, [extraToggle.key]: e.target.checked })}
                  data-testid={`admin-${testKey}-toggle-${extraToggle.key}`}
                  className="h-4 w-4 accent-npbc-red"
                />
                <span className="text-sm">{extraToggle.label}</span>
              </label>
            )}
          </div>
          <div className="mt-8 flex justify-end gap-3">
            <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-black/10 px-5 py-2.5 text-sm font-semibold hover:bg-npbc-cream">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={busy} data-testid={`admin-${testKey}-save`} className="btn-primary !py-2.5 !text-sm disabled:opacity-60">
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-serif text-2xl">{title}</h2>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full hover:bg-npbc-cream">
              <X className="h-4 w-4" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------- Simple read-only list ----------------
function SimpleList({ endpoint, title, fields }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data || [])).catch(() => {}); }, [endpoint]);

  const fmt = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : v ?? "—");

  return (
    <div>
      <h1 className="heading-serif text-4xl">{title}</h1>
      <p className="mt-1 text-sm text-npbc-gray">{items.length} total</p>

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="rounded-2xl bg-white border border-black/[0.05] p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-npbc-gray/70">
              {new Date(item.created_at).toLocaleString()}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-npbc-red">{f.replace(/_/g, " ")}</div>
                  <div className="mt-1 text-sm text-npbc-black whitespace-pre-wrap break-words">{fmt(item[f])}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="rounded-2xl bg-white border border-dashed border-black/10 p-12 text-center text-npbc-gray/70">
            No entries yet.
          </div>
        )}
      </div>
    </div>
  );
}
